import * as i1$2 from '@angular/cdk/a11y';
import { LiveAnnouncer, FocusKeyManager, CdkTrapFocus, A11yModule } from '@angular/cdk/a11y';
import { ScrollingModule } from '@angular/cdk/scrolling';
import * as i3 from '@angular/common';
import { NgTemplateOutlet, CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, HostBinding, Directive, forwardRef, ElementRef, HostListener, Inject, ViewEncapsulation, Component, Renderer2, ANIMATION_MODULE_TYPE, ViewChild, ViewChildren, Injectable, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { SelectControlValueAccessor, NG_VALUE_ACCESSOR, Validators, NgControl, NgModel, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, BehaviorSubject, noop, distinctUntilChanged } from 'rxjs';
import { distinctUntilChanged as distinctUntilChanged$1 } from 'rxjs/operators';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import * as i1$1 from '@angular/cdk/overlay';
import { OverlayConfig, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import * as i1 from '@angular/animations';
import { style, animate } from '@angular/animations';
import { _getFocusedElementPierceShadowDom } from '@angular/cdk/platform';

const NEGATIVE_VALUE$1 = -1;
const COUNTER_LIMIT = 99;
const HEIGHT_256 = 256;
class WindmillSelectPropertiesDirective extends SelectControlValueAccessor {
    /**
     * Property used to set different sizes for select
     */
    size;
    /**
     * Placeholder text for component
     */
    placeholder;
    /**
     * Property can be used to set a clear icon for select
     */
    hasClearIcon = false;
    /**
     * Property can be used to set a customizable message in case the list is empty
     */
    emptyMessage;
    /**
     * Property is used to enable multiple selection for the component
     */
    multiple = false;
    /**
     * Property can be used to set a custom validation function for errors
     */
    validationFunction;
    /**
     * Sets the aria-label attribute for the select
     */
    ariaLabel;
    /**
     * Sets the aria-description attribute for the select
     */
    ariaDescription;
    /**
     * Sets the aria-labelledby attribute for the select
     */
    ariaLabelledby;
    /**
     * Sets the aria-describedby attribute for the select
     */
    ariaDescribedby;
    /**
     * Sets the aria-label attribute for the clear button
     */
    ariaLabelClearButton;
    /**
     * Sets the aria-label attribute for the list
     */
    ariaLabelList;
    /**
     * Sets the aria-description attribute for the list
     */
    ariaDescriptionList;
    /**
     * Sets the aria-description attribute for the select all checkbox
     */
    ariaDescriptionSelectAllCheckbox;
    /**
     * Sets the aria-description attribute for the list item
     */
    ariaDescriptionListItem;
    /**
     * Sets the aria-labelledby attribute for the list
     */
    ariaLabelledbyList;
    /**
     * Sets the aria-describedby attribute for the list
     */
    ariaDescribedbyList;
    /**
     * Sets the aria-label attribute for the counter
     */
    ariaLabelCounter;
    /**
     * Sets the data-testid attribute for the select
     */
    testInputId;
    /**
     * Sets the data-testid attribute for the list
     */
    testListId;
    /**
     * Sets the id attribute for the list
     */
    listId;
    /**
     * Sets the id attribute for the select
     */
    id;
    /**
     * Sets the title attribute for the select
     */
    title;
    /**
     * Property is used to set custom class that can be added to the component overlay.
     */
    overlayClass;
    /**
     * Property can be used to set a maximum height for the list
     */
    maxHeight = HEIGHT_256;
    /**
     * Property can be used to add a counter indicator
     */
    counterNumber = false;
    /**
     * Property used to set a label for select all checkbox option
     */
    selectAllLabel = 'Select all';
    /**
     * Property is used to set which property of the source will be displayed in the select
     */
    get displayPropertyName() {
        return this.allowGroups ? null : this._displayPropertyName;
    }
    set displayPropertyName(value) {
        this._displayPropertyName = value;
    }
    /**
     * Property is used to set which source property will be set as the input value
     */
    get valuePropertyName() {
        return this.allowGroups ? null : this._valuePropertyName;
    }
    set valuePropertyName(value) {
        this._valuePropertyName = value;
    }
    /**
     * Property is used to allow groups for the component
     */
    get allowGroups() {
        return this._allowGroups;
    }
    set allowGroups(value) {
        this._allowGroups = value;
    }
    /**
     * Property can be used to send the list of items to the component
     */
    get source() {
        return this._source;
    }
    set source(value) {
        this._source = value;
    }
    /**
     * Sets the disabled attribute on select
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(isDisabled) {
        this.setDisabledState(isDisabled);
    }
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the select in an editable state
     */
    backgroundColor;
    /**
     * Emits when the counter value changes after selecting items from the list
     */
    counterValueChange = new EventEmitter();
    /**
     * Emits when the overlay open state is changed
     */
    openedChange = new EventEmitter();
    /**
     * Emits when the items selected from the list change
     */
    selectionChange = new EventEmitter();
    /**
     * Emits when scrolling to the bottom of the list
     */
    scrolledToBottom = new EventEmitter();
    selectListComponentRef;
    selectedItems = [];
    injectedFormControl;
    isOpened = false;
    panelClass = '';
    displayedValue;
    closedOverlaySubscription = Subscription.EMPTY;
    selectionChangeSubscription = Subscription.EMPTY;
    scrolledToBottomSubscription = Subscription.EMPTY;
    resizeObserver;
    focusOrigin = null;
    _source;
    _disabled = false;
    _selectAll = false;
    _readonly = false;
    _allowGroups = false;
    _displayPropertyName;
    _valuePropertyName;
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    getDisplayedComplexValue(item) {
        return String(item[this.displayPropertyName]);
    }
    getValueComplex(item) {
        return this.valuePropertyName ? item[this.valuePropertyName] : item;
    }
    getDisplayedSelectedValue(item) {
        return this.allowGroups ? item.name : this.getDisplayedSelectedValueNoGroups(item);
    }
    getDisplayedSelectedValueNoGroups(item) {
        return this.hasComplexSource ? this.getDisplayedComplexValue(item) : item;
    }
    getFormControlSelectedValue(item) {
        return this.allowGroups ? item.value : this.getFormControlSelectedValueNoGroups(item);
    }
    getFormControlSelectedValueNoGroups(item) {
        return this.hasComplexSource ? this.getValueComplex(item) : this.value;
    }
    get shouldShowValue() {
        return (typeof this.displayedValue !== 'undefined' &&
            this.displayedValue !== null &&
            this.displayedValue !== '' &&
            this.displayedValue.toString() !== '');
    }
    get hasComplexSource() {
        const propertiesAreDefined = typeof this.displayPropertyName !== 'undefined';
        const propertiesAreNotEmptyStrings = this.displayPropertyName !== '';
        const propertiesAreNotNull = this.displayPropertyName !== null;
        return !this.allowGroups && propertiesAreDefined && propertiesAreNotNull && propertiesAreNotEmptyStrings;
    }
    get hasEmptyMessage() {
        return this.emptyMessage !== null && typeof this.emptyMessage !== 'undefined' && this.emptyMessage !== '';
    }
    get arrowIconSize() {
        return this.size === 'extrasmall' ? '20' : '24';
    }
    get hasInvalidState() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get disabledAttributeValue() {
        return this.disabled ? true : null;
    }
    get hasItemsDisplayed() {
        return typeof this.hasEmptyMessage !== 'undefined' && this.source?.length === 0;
    }
    get shouldShowCounter() {
        return (this.multiple &&
            this.counterNumber &&
            (Number(this.counterValue) > 0 || this.reachedMaxLengthCounter) &&
            this.displayedValue !== '' &&
            this.displayedValue !== null &&
            typeof this.displayedValue !== 'undefined' &&
            this.displayedValue?.length !== 0);
    }
    get counterValue() {
        let value;
        if (this.selectedItems?.length > 0) {
            value = String(this.selectedItems?.length);
        }
        else if (this.displayedValue === '' || this.displayedValue === null) {
            value = '0';
        }
        else {
            value = String(this.displayedValue).split(',')?.length.toString();
        }
        if (Number(value) > COUNTER_LIMIT) {
            value = '99+';
        }
        return value;
    }
    get reachedMaxLengthCounter() {
        return this.selectedItems?.length > COUNTER_LIMIT;
    }
    get ariaExpandedValue() {
        return this._readonly ? null : this.isOpened;
    }
    get backgroundColorValue() {
        return this._readonly || this.disabled ? null : this.backgroundColor;
    }
    get nonEditablePlaceholder() {
        const hasValue = (!Array.isArray(this.value) && (this.value || this.value === 0)) || (Array.isArray(this.value) && this.value.length !== 0);
        return hasValue ? null : this._readonly ? '-' : this.placeholder;
    }
    get shouldShowClearButton() {
        const isDisplayedValueEmpty = this.displayedValue === '' || this.displayedValue === null || typeof this.displayedValue === 'undefined';
        const hasValidDisplayedValue = !isDisplayedValueEmpty && this.isValidValue(this.displayedValue) && this.displayedValue?.length !== 0;
        return this.hasClearIcon && hasValidDisplayedValue && !this.disabled && !this._readonly;
    }
    isValidValue(valueToCheck) {
        return typeof valueToCheck !== 'undefined';
    }
    getItemByFormControlValue() {
        if (this.multiple) {
            return this.value;
        }
        if (this.hasComplexSource) {
            return this.source?.find(selectedItem => this.doesValueExistsInSource(selectedItem, this.value));
        }
        return this.selectedItems.find(selectedItem => this.value === selectedItem);
    }
    getGroupListItems() {
        const groupItems = [];
        this.source?.forEach(group => {
            if (!group.disabled) {
                group.items.forEach(item => {
                    groupItems.push(item);
                });
            }
        });
        return groupItems;
    }
    findValueInSource(value) {
        if (this.allowGroups) {
            return this.findItemInGroups(value)?.value;
        }
        return this.findItemInSource(value);
    }
    findItemInSource(valueItem) {
        let elementSearched;
        this.source?.forEach(element => {
            if (element === valueItem) {
                elementSearched = element;
            }
            if (this.hasComplexSource) {
                const foundElement = this.findElementInComplexSource(element, valueItem);
                if (foundElement !== null) {
                    elementSearched = foundElement;
                }
            }
        });
        return elementSearched;
    }
    findItemInGroups(valueItem) {
        let groupItem;
        this.source?.forEach(element => {
            const searchItem = element.items?.find((item) => item.value === valueItem);
            if (searchItem) {
                groupItem = searchItem;
            }
        });
        return groupItem;
    }
    getIndexOfDisplayedValue() {
        if (this.allowGroups) {
            const groupItemsList = this.getGroupListItems();
            return groupItemsList.findIndex(item => item.name === this.displayedValue);
        }
        return this.source
            ? this.source.findIndex(item => this.hasComplexSource
                ? this.getDisplayedComplexValue(item) === String(this.displayedValue)
                : item === this.displayedValue)
            : NEGATIVE_VALUE$1;
    }
    updateDisplayedValueForComplexSource() {
        if (this.hasComplexSource) {
            const displayedItem = this.getItemByFormControlValue();
            if (displayedItem && typeof displayedItem !== 'undefined' && displayedItem !== null) {
                if (this.multiple && Array.isArray(displayedItem)) {
                    const result = [];
                    displayedItem.forEach(selectedItemValue => {
                        const selectedItem = this.source?.find(item => this.getValueComplex(item) === selectedItemValue);
                        result.push(this.getDisplayedComplexValue(selectedItem));
                    });
                    this.displayedValue = result.join(', ');
                }
                else {
                    this.displayedValue = displayedItem[this.displayPropertyName];
                }
            }
        }
    }
    doesValueExistsInSource(item, value) {
        const itemValue = this.getValueComplex(item);
        const isValueObject = value && typeof value === 'object';
        if (isValueObject) {
            return ((this.valuePropertyName && itemValue[this.valuePropertyName] === value[this.valuePropertyName]) ||
                (this.displayPropertyName && itemValue[this.displayPropertyName] === value[this.displayPropertyName]));
        }
        return itemValue === value;
    }
    getSelectListProperties(componentProperties) {
        return {
            focusOriginSelectButton: componentProperties.focusOrigin,
            isOpened: this.isOpened,
            width: componentProperties.width,
            maxHeight: this.maxHeight,
            elementRef: componentProperties.elementRef,
            allowGroups: this.allowGroups,
            multiple: this.multiple,
            selectAll: componentProperties.selectAll,
            selectAllLabel: this.selectAllLabel,
            displayedValue: this.displayedValue,
            overlayClass: this.overlayClass,
            sourceProperties: {
                source: this.source,
                displayPropertyName: this.displayPropertyName,
                valuePropertyName: this.valuePropertyName,
                emptyMessage: this.emptyMessage
            },
            accessibilityProperties: this.getAccessibilityProperties(),
            listIdProperties: {
                testListId: this.testListId,
                listId: this.listId
            }
        };
    }
    getAccessibilityProperties() {
        return {
            ariaDescriptionSelectAllCheckbox: this.ariaDescriptionSelectAllCheckbox,
            ariaLabelList: this.ariaLabelList,
            ariaDescriptionList: this.ariaDescriptionList,
            ariaLabelledbyList: this.ariaLabelledbyList,
            ariaDescribedbyList: this.ariaDescribedbyList,
            ariaDescriptionListItem: this.ariaDescriptionListItem
        };
    }
    isObjectAndNoValueProperty(elementDisplayValue, valueItem) {
        return (typeof valueItem === 'object' &&
            valueItem !== null &&
            !this.valuePropertyName &&
            elementDisplayValue === this.getDisplayedComplexValue(valueItem));
    }
    isValuePropertyAndNotObject(elementDisplayValue, valueItem) {
        return Boolean(this.valuePropertyName &&
            typeof valueItem !== 'object' &&
            valueItem &&
            elementDisplayValue === this.getDisplayedComplexValue(valueItem));
    }
    findElementInComplexSource(element, valueItem) {
        if (!element) {
            return null;
        }
        const elementDisplayValue = this.getDisplayedComplexValue(element);
        const elementValueProperty = this.getValueComplex(element);
        if (this.isObjectAndNoValueProperty(elementDisplayValue, valueItem) ||
            this.isValuePropertyAndNotObject(elementDisplayValue, valueItem)) {
            return element;
        }
        if (elementValueProperty === valueItem) {
            return elementValueProperty;
        }
        return null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectPropertiesDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillSelectPropertiesDirective, isStandalone: true, selector: "[windmillSelectProperties]", inputs: { size: "size", placeholder: "placeholder", hasClearIcon: "hasClearIcon", emptyMessage: "emptyMessage", multiple: "multiple", validationFunction: "validationFunction", ariaLabel: "ariaLabel", ariaDescription: "ariaDescription", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", ariaLabelClearButton: "ariaLabelClearButton", ariaLabelList: "ariaLabelList", ariaDescriptionList: "ariaDescriptionList", ariaDescriptionSelectAllCheckbox: "ariaDescriptionSelectAllCheckbox", ariaDescriptionListItem: "ariaDescriptionListItem", ariaLabelledbyList: "ariaLabelledbyList", ariaDescribedbyList: "ariaDescribedbyList", ariaLabelCounter: "ariaLabelCounter", testInputId: "testInputId", testListId: "testListId", listId: "listId", id: "id", title: "title", overlayClass: "overlayClass", maxHeight: "maxHeight", counterNumber: "counterNumber", selectAllLabel: "selectAllLabel", displayPropertyName: "displayPropertyName", valuePropertyName: "valuePropertyName", allowGroups: "allowGroups", source: "source", disabled: "disabled", backgroundColor: "backgroundColor" }, outputs: { counterValueChange: "counterValueChange", openedChange: "openedChange", selectionChange: "selectionChange", scrolledToBottom: "scrolledToBottom" }, host: { properties: { "class": "this.size" } }, usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectPropertiesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmillSelectProperties]',
                    standalone: true
                }]
        }], propDecorators: { size: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], placeholder: [{
                type: Input
            }], hasClearIcon: [{
                type: Input
            }], emptyMessage: [{
                type: Input
            }], multiple: [{
                type: Input
            }], validationFunction: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelClearButton: [{
                type: Input
            }], ariaLabelList: [{
                type: Input
            }], ariaDescriptionList: [{
                type: Input
            }], ariaDescriptionSelectAllCheckbox: [{
                type: Input
            }], ariaDescriptionListItem: [{
                type: Input
            }], ariaLabelledbyList: [{
                type: Input
            }], ariaDescribedbyList: [{
                type: Input
            }], ariaLabelCounter: [{
                type: Input
            }], testInputId: [{
                type: Input
            }], testListId: [{
                type: Input
            }], listId: [{
                type: Input
            }], id: [{
                type: Input
            }], title: [{
                type: Input
            }], overlayClass: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], counterNumber: [{
                type: Input
            }], selectAllLabel: [{
                type: Input
            }], displayPropertyName: [{
                type: Input
            }], valuePropertyName: [{
                type: Input
            }], allowGroups: [{
                type: Input
            }], source: [{
                type: Input
            }], disabled: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], counterValueChange: [{
                type: Output
            }], openedChange: [{
                type: Output
            }], selectionChange: [{
                type: Output
            }], scrolledToBottom: [{
                type: Output
            }] } });

const NEGATIVE_VALUE = -1;
const ITEM_SIZE = 48;
class WindmillSelectListPropertiesDirective {
    width;
    maxHeight;
    multiple = false;
    isOpen = true;
    hasFocus = false;
    sourceProperties;
    accessibilityProperties;
    listIdProperties;
    itemSize = ITEM_SIZE;
    allowGroups = false;
    selectedItems = [];
    selectAll = false;
    selectAllLabel;
    isSelectAllIndeterminate = false;
    isSelectAllChecked = false;
    fadeInFactory;
    fadeOutFactory;
    fadeInPlayer;
    fadeOutPlayer;
    selectionChange = new EventEmitter();
    scrolledToBottom = new EventEmitter();
    activeItemIndexSubscription = Subscription.EMPTY;
    preselectedItemsSubscription = Subscription.EMPTY;
    queryListChangesSubscription = Subscription.EMPTY;
    overlayOpenSubscription = Subscription.EMPTY;
    keyManager;
    destroyExistingAnimationPlayers() {
        this.fadeOutPlayer?.finish();
        this.fadeInPlayer?.finish();
        this.fadeOutPlayer?.destroy();
        this.fadeInPlayer?.destroy();
    }
    get hasEmptyMessage() {
        return (typeof this.sourceProperties.emptyMessage !== 'undefined' &&
            (this.sourceProperties.source === null || this.sourceProperties.source?.length === 0));
    }
    get hasComplexSource() {
        const propertiesAreDefined = typeof this.sourceProperties.displayPropertyName !== 'undefined';
        const propertiesAreNotEmptyStrings = this.sourceProperties.displayPropertyName !== '';
        const propertiesAreNotNull = this.sourceProperties.displayPropertyName !== null;
        return !this.allowGroups && propertiesAreDefined && propertiesAreNotEmptyStrings && propertiesAreNotNull;
    }
    get selectListHeight() {
        return this.sourceProperties.source && this.sourceProperties.source.length > 0
            ? this.sourceProperties.source.length * this.itemSize
            : this.itemSize;
    }
    get focusedItemIndex() {
        return this.keyManager.activeItemIndex;
    }
    getNameFor(item) {
        return item && this.sourceProperties.displayPropertyName ? this.getDisplayedValue(item) : item;
    }
    getValueFor(item) {
        return item && this.sourceProperties.valuePropertyName ? this.getValueComplex(item) : item;
    }
    getDisplayedValue(item) {
        return typeof item === 'object' && item
            ? String(item[this.sourceProperties.displayPropertyName])
            : item;
    }
    getValueComplex(item) {
        return item && this.sourceProperties.valuePropertyName
            ? item[this.sourceProperties.valuePropertyName]
            : item;
    }
    getGroupItemValue(item) {
        return item.value;
    }
    isItemSelected(item) {
        if (this.allowGroups) {
            return this.isGroupItemSelected(item);
        }
        else if (this.hasComplexSource) {
            return this.isComplexItemSelected(item);
        }
        return typeof this.selectedItems?.find(selectedItem => item === selectedItem) !== 'undefined';
    }
    sortItemsByInitialSourceIndex(array) {
        return array.sort((firstItem, secondItem) => {
            const firstItemIndex = this.getSourceItemIndex(firstItem);
            const secondItemIndex = this.getSourceItemIndex(secondItem);
            return firstItemIndex - secondItemIndex;
        });
    }
    getSourceItemIndex(item) {
        if (this.allowGroups) {
            const groupItems = [];
            this.sourceProperties.source?.forEach(group => {
                group.items?.forEach(item => {
                    groupItems.push(item);
                });
            });
            return groupItems.findIndex(listItem => listItem.value === item);
        }
        else if (this.hasComplexSource && this.sourceProperties.source) {
            return this.sourceProperties.source.findIndex(listItem => this.getValueComplex(listItem) === item);
        }
        return this.sourceProperties.source ? this.sourceProperties.source.findIndex(listItem => listItem === item) : NEGATIVE_VALUE;
    }
    isGroupItemSelected(item) {
        if (this.multiple) {
            return typeof this.selectedItems?.find(selectedItem => this.getGroupItemValue(item) === selectedItem) !== 'undefined';
        }
        return typeof this.selectedItems?.find(selectedItem => this.getGroupItemValue(item) === selectedItem) !== 'undefined';
    }
    isComplexItemSelected(item) {
        if (this.multiple) {
            return typeof this.selectedItems?.find(selectedItem => this.getValueComplex(item) === selectedItem) !== 'undefined';
        }
        return (typeof this.selectedItems?.find(selectedItem => this.getDisplayedValue(item) === this.getDisplayedValue(selectedItem)) !==
            'undefined');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectListPropertiesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillSelectListPropertiesDirective, isStandalone: true, selector: "[windmillSelectListProperties]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectListPropertiesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmillSelectListProperties]',
                    standalone: true
                }]
        }] });

class WindmillListItemComponent {
    _elementRef;
    name;
    multiple = false;
    isSelected = false;
    isIndeterminate = false;
    isDisabled = false;
    hasSelectAll = false;
    multipleSelection = new EventEmitter();
    singleSelection = new EventEmitter();
    selectAllChanged = new EventEmitter();
    elementRef;
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        this.elementRef = this._elementRef;
    }
    onKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            if (event.target?.firstElementChild?.classList.contains('select-all-wrapper')) {
                this.onSelectAllClick();
            }
            else {
                this.onClick();
            }
        }
    }
    onSelectAllClick() {
        this.onClick();
        this.selectAllChanged.emit(this.isSelected);
    }
    onClick() {
        this.toggleSelectionStatus();
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    get shouldApplySelectedState() {
        return this.isSelected || this.isIndeterminate;
    }
    toggleSelectionStatus() {
        if (!this.isDisabled) {
            this.isSelected = !this.isSelected;
            if (this.multiple) {
                this.multipleSelection.emit(this.isSelected);
            }
            else {
                this.singleSelection.emit(this.isSelected);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillListItemComponent, deps: [{ token: forwardRef(() => ElementRef) }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillListItemComponent, isStandalone: true, selector: "windmill-list-item", inputs: { name: "name", multiple: "multiple", isSelected: "isSelected", isIndeterminate: "isIndeterminate", isDisabled: "isDisabled", hasSelectAll: "hasSelectAll" }, outputs: { multipleSelection: "multipleSelection", singleSelection: "singleSelection", selectAllChanged: "selectAllChanged" }, host: { listeners: { "keydown": "onKeydown($event)" } }, ngImport: i0, template: "@if (hasSelectAll) {\r\n  <div\r\n    class=\"list-item-container select-all-wrapper\"\r\n    [class.is-selected]=\"shouldApplySelectedState\"\r\n    (click)=\"onSelectAllClick()\">\r\n    <div\r\n      class=\"windmill-pseudo-checkbox\"\r\n      [class.is-checked]=\"isSelected\"\r\n      [class.is-indeterminate]=\"isIndeterminate\">\r\n      <ng-template [ngTemplateOutlet]=\"checkboxContainer\"></ng-template>\r\n    </div>\r\n    <ng-template [ngTemplateOutlet]=\"checkboxLabel\"></ng-template>\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"list-item-container\"\r\n    (click)=\"onClick()\">\r\n    @if (multiple) {\r\n      <div\r\n        class=\"windmill-pseudo-checkbox\"\r\n        [class.is-checked]=\"isSelected\"\r\n        [class.is-disabled]=\"isDisabled\">\r\n        <ng-template [ngTemplateOutlet]=\"checkboxContainer\"></ng-template>\r\n      </div>\r\n    }\r\n    <ng-template [ngTemplateOutlet]=\"checkboxLabel\"></ng-template>\r\n  </div>\r\n}\r\n\r\n<ng-template #checkboxContainer>\r\n  <span class=\"checkbox-inner-container\">\r\n    <span class=\"checkbox-frame\"></span>\r\n    <span class=\"checkbox-background\"></span>\r\n  </span>\r\n</ng-template>\r\n\r\n<ng-template #checkboxLabel>\r\n  <span class=\"list-item-name\">\r\n    {{ name }}\r\n  </span>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillListItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-list-item', encapsulation: ViewEncapsulation.None, imports: [NgTemplateOutlet], standalone: true, template: "@if (hasSelectAll) {\r\n  <div\r\n    class=\"list-item-container select-all-wrapper\"\r\n    [class.is-selected]=\"shouldApplySelectedState\"\r\n    (click)=\"onSelectAllClick()\">\r\n    <div\r\n      class=\"windmill-pseudo-checkbox\"\r\n      [class.is-checked]=\"isSelected\"\r\n      [class.is-indeterminate]=\"isIndeterminate\">\r\n      <ng-template [ngTemplateOutlet]=\"checkboxContainer\"></ng-template>\r\n    </div>\r\n    <ng-template [ngTemplateOutlet]=\"checkboxLabel\"></ng-template>\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"list-item-container\"\r\n    (click)=\"onClick()\">\r\n    @if (multiple) {\r\n      <div\r\n        class=\"windmill-pseudo-checkbox\"\r\n        [class.is-checked]=\"isSelected\"\r\n        [class.is-disabled]=\"isDisabled\">\r\n        <ng-template [ngTemplateOutlet]=\"checkboxContainer\"></ng-template>\r\n      </div>\r\n    }\r\n    <ng-template [ngTemplateOutlet]=\"checkboxLabel\"></ng-template>\r\n  </div>\r\n}\r\n\r\n<ng-template #checkboxContainer>\r\n  <span class=\"checkbox-inner-container\">\r\n    <span class=\"checkbox-frame\"></span>\r\n    <span class=\"checkbox-background\"></span>\r\n  </span>\r\n</ng-template>\r\n\r\n<ng-template #checkboxLabel>\r\n  <span class=\"list-item-name\">\r\n    {{ name }}\r\n  </span>\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }], propDecorators: { name: [{
                type: Input
            }], multiple: [{
                type: Input
            }], isSelected: [{
                type: Input
            }], isIndeterminate: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], hasSelectAll: [{
                type: Input
            }], multipleSelection: [{
                type: Output
            }], singleSelection: [{
                type: Output
            }], selectAllChanged: [{
                type: Output
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const NEGATIVE_ONE = -1;
const GROUP_NAME_HEIGHT = 40;
const FADE_ANIMATION_DURATION = 250;
class WindmillSelectListComponent extends WindmillSelectListPropertiesDirective {
    injector;
    changeDetector;
    animationBuilder;
    queryItemsList;
    selectListWrapper;
    preselectedItems$;
    overlayOpen$;
    animationDone$;
    animationMode;
    elementRef;
    renderer;
    liveAnnouncer;
    hasSelectAll = true;
    constructor(injector, changeDetector, animationBuilder, animationMode) {
        super();
        this.injector = injector;
        this.changeDetector = changeDetector;
        this.animationBuilder = animationBuilder;
        this.renderer = this.injector.get(Renderer2);
        this.elementRef = this.injector.get(ElementRef);
        this.liveAnnouncer = this.injector.get(LiveAnnouncer);
        this.animationMode = animationMode;
        this.initializeBehaviorSubjects();
    }
    ngOnInit() {
        if (this.animationMode !== 'NoopAnimations') {
            this.subscribeToOverlayOpenForAnimation();
        }
        this.subscribeToPreselectedItems();
    }
    ngAfterViewInit() {
        this.setListWidthInHtml();
        this.registerPanelScrollEvent();
        this.initializeKeyManager();
        this.handleFocus();
        this.announceEmptyMessage();
        this.subscribeToListChanges();
    }
    ngOnDestroy() {
        this.activeItemIndexSubscription.unsubscribe();
        this.preselectedItemsSubscription.unsubscribe();
        this.queryListChangesSubscription?.unsubscribe();
        this.overlayOpenSubscription?.unsubscribe();
    }
    initializeBehaviorSubjects() {
        this.preselectedItems$ = new BehaviorSubject([]);
        this.overlayOpen$ = new BehaviorSubject(this.isOpen);
        this.animationDone$ = new BehaviorSubject(false);
    }
    handleFocus() {
        this.initializeTabIndex();
        this.updateKeyManagerActiveItem();
    }
    subscribeToListChanges() {
        this.queryListChangesSubscription = this.queryItemsList?.changes.subscribe((changes) => {
            changes.toArray().forEach(item => {
                if (!item.isDisabled) {
                    this.renderer.setAttribute(item.elementRef.nativeElement, 'tabindex', '-1');
                }
            });
        });
    }
    onKeyboardChange(event) {
        if (this.hasEmptyMessage) {
            return;
        }
        this.keyManager?.onKeydown(event);
    }
    get sourceGroups() {
        return this.sourceProperties.source;
    }
    initializeTabIndex() {
        this.queryItemsList.toArray().forEach((item, index) => {
            if (!item.isDisabled) {
                const preselectedItemIndex = this.getSelectedIndex();
                const selectedItemIndex = preselectedItemIndex > NEGATIVE_ONE ? preselectedItemIndex : 0;
                const actualTabIndex = index === selectedItemIndex ? '0' : '-1';
                this.renderer.setAttribute(item.elementRef.nativeElement, 'tabindex', actualTabIndex);
            }
        });
    }
    onFocusIn(event) {
        if (!this.hasFocus) {
            this.hasFocus = true;
            this.queryItemsList.toArray().forEach(item => {
                if (!item.isDisabled) {
                    this.renderer.setAttribute(item.elementRef.nativeElement, 'tabindex', '-1');
                }
            });
            const selectedItemIndex = this.getSelectedIndex() > NEGATIVE_ONE ? this.getSelectedIndex() : 0;
            if (event.relatedTarget !== null) {
                this.keyManager.setActiveItem(selectedItemIndex);
            }
        }
    }
    onFocusOut() {
        setTimeout(() => {
            if (!this.containsFocus()) {
                this.hasFocus = false;
                this.resetTabIndex();
            }
        });
    }
    resetTabIndex() {
        const selectedItemIndex = this.getSelectedIndex() > NEGATIVE_ONE ? this.getSelectedIndex() : 0;
        const item = this.queryItemsList.toArray()[selectedItemIndex].elementRef.nativeElement;
        const isItemDisabled = this.queryItemsList.toArray()[selectedItemIndex].isDisabled;
        if (item && !isItemDisabled) {
            this.renderer.setAttribute(item, 'tabindex', '0');
        }
    }
    onListItemKeydown(event) {
        if (event.key === ' ') {
            event.preventDefault();
        }
        if (event.key === 'ArrowUp' && this.allowGroups && this.keyManager.activeItemIndex === 1) {
            const listHtmlElement = this.selectListWrapper.nativeElement.querySelector('ul');
            if (listHtmlElement?.scrollTop === GROUP_NAME_HEIGHT) {
                listHtmlElement.scrollTop = 0;
            }
        }
    }
    onMultipleSelection(isItemChecked, item) {
        if (isItemChecked) {
            this.updateSelectedItems(item);
        }
        else {
            const indexOfItem = this.getIndexOfItem(item);
            if (indexOfItem !== NEGATIVE_ONE) {
                this.sortAndEmitSelectedItems(indexOfItem);
            }
        }
        if (this.selectAll) {
            this.updateSelectAll();
        }
    }
    sortAndEmitSelectedItems(indexItem) {
        this.selectedItems.splice(indexItem, 1);
        const mappedSelectedItems = this.selectedItems.map(item => (this.hasComplexSource ? item : this.getNameFor(item)));
        this.selectedItems = this.sortItemsByInitialSourceIndex(mappedSelectedItems);
        this.selectionChange.emit(this.selectedItems);
    }
    onSelectAllChange(areAllSelected) {
        const queryItemsWithoutSelectAll = this.queryItemsList
            .toArray()
            .filter(item => !item.hasSelectAll);
        queryItemsWithoutSelectAll.forEach((item) => {
            item.isSelected = areAllSelected;
            if (areAllSelected) {
                this.addItemsToSelectedItems(item);
            }
            else {
                this.selectedItems = [];
            }
        });
        this.selectedItems = [...new Set(this.selectedItems)];
        this.updateSelectAll();
        this.selectedItems = this.sortItemsByInitialSourceIndex(this.selectedItems);
        this.selectionChange.emit(this.selectedItems);
    }
    addItemsToSelectedItems(item) {
        if (this.hasComplexSource) {
            const itemSource = this.sourceProperties.source?.find(itemSource => this.getDisplayedValue(itemSource) === item.name);
            this.selectedItems.push(this.getValueComplex(itemSource));
        }
        else if (this.allowGroups) {
            this.addGroupItemToSelectedItems(item);
        }
        else {
            this.selectedItems.push(item.name);
        }
    }
    addGroupItemToSelectedItems(item) {
        const groupItems = this.sourceProperties.source
            .map(itemSource => itemSource.items)
            .find(child => child.find((itemGroup) => itemGroup.name === item.name));
        groupItems?.forEach(groupItem => {
            this.selectedItems.push(groupItem.value);
        });
    }
    updateSelectedItems(item) {
        const valueComplexItem = this.getValueComplex(item);
        const valueItem = this.hasComplexSource ? valueComplexItem : item;
        if (this.multiple) {
            this.addItemToSelectedItems(valueItem);
        }
        else {
            const newSelectedItem = this.allowGroups ? this.getGroupItemValue(valueItem) : valueItem;
            this.selectedItems = [newSelectedItem];
        }
        this.selectionChange.emit(this.selectedItems);
    }
    addItemToSelectedItems(valueItem) {
        const newSelectedItem = this.allowGroups ? this.getGroupItemValue(valueItem) : valueItem;
        this.selectedItems.push(newSelectedItem);
        this.selectedItems = this.sortItemsByInitialSourceIndex(this.selectedItems);
    }
    setListWidthInHtml() {
        const sectionElement = this.elementRef.nativeElement.querySelector('.select-list-wrapper');
        if (sectionElement) {
            this.renderer.setStyle(sectionElement, 'width', `${this.width?.toString()}px`);
        }
    }
    announceEmptyMessage() {
        if (this.sourceProperties.emptyMessage && this.hasEmptyMessage) {
            this.liveAnnouncer.announce(this.sourceProperties.emptyMessage).catch(() => noop);
        }
    }
    getSelectedIndex() {
        return this.queryItemsList.toArray().findIndex(sourceItem => sourceItem.isSelected);
    }
    getSelectAllAriaChecked() {
        return this.isSelectAllIndeterminate ? 'mixed' : this.isSelectAllChecked;
    }
    getAriaChecked(item) {
        return this.multiple ? this.isItemSelected(item) : null;
    }
    getAriaSelected(item) {
        return this.multiple ? null : this.isItemSelected(item);
    }
    isDisabled(groupItem) {
        return groupItem['disabled'] ?? false;
    }
    updateSelectAll() {
        if (this.multiple && this.selectAll && Array.isArray(this.selectedItems)) {
            const numberItems = this.allowGroups ? this.getNumberItemsGroups() : this.sourceProperties.source?.length;
            switch (this.selectedItems.length) {
                case numberItems:
                    this.isSelectAllChecked = true;
                    this.isSelectAllIndeterminate = false;
                    break;
                case 0:
                    this.isSelectAllChecked = false;
                    this.isSelectAllIndeterminate = false;
                    break;
                default:
                    this.isSelectAllChecked = false;
                    this.isSelectAllIndeterminate = true;
                    break;
            }
            this.changeDetector.detectChanges();
        }
    }
    getNumberItemsGroups() {
        if (this.allowGroups) {
            const source = this.sourceProperties.source;
            let children = 0;
            source?.forEach(itemSource => {
                if (!itemSource.disabled) {
                    children += itemSource.items?.length;
                }
            });
            return children;
        }
        return 0;
    }
    registerPanelScrollEvent() {
        if (this.selectListWrapper) {
            const listHtmlElement = this.selectListWrapper.nativeElement.querySelector('ul');
            if (listHtmlElement) {
                listHtmlElement.addEventListener('scroll', event => {
                    if (listHtmlElement.scrollTop + listHtmlElement.offsetHeight >= listHtmlElement.scrollHeight) {
                        this.scrolledToBottom.emit(event);
                    }
                });
            }
        }
    }
    initializeKeyManager() {
        const availableItems = this.queryItemsList.filter(item => !item.isDisabled);
        this.queryItemsList.reset([availableItems]);
        this.keyManager = new FocusKeyManager(this.queryItemsList).withHomeAndEnd(true);
    }
    updateKeyManagerActiveItem() {
        const queryItemToBeFocused = this.queryItemsList?.find(queryItem => this.multiple
            ? queryItem.isSelected
            : this.hasComplexSource
                ? this.selectedItems.findIndex(selectedItem => this.getDisplayedValue(selectedItem) === queryItem.name) > NEGATIVE_ONE
                : this.selectedItems.findIndex(selectedItem => selectedItem === queryItem.name) > NEGATIVE_ONE);
        if (this.selectedItems && queryItemToBeFocused) {
            this.keyManager.setActiveItem(queryItemToBeFocused);
        }
        else {
            this.keyManager.setFirstItemActive();
        }
    }
    handleFocusOnSourceChange() {
        this.queryListChangesSubscription = this.queryItemsList?.changes.subscribe(() => {
            setTimeout(() => {
                if (this.selectedItems?.length === 0) {
                    this.keyManager?.setFirstItemActive();
                    return;
                }
                this.updateKeyManager();
            });
        });
    }
    updateKeyManager() {
        const selectedItemName = this.hasComplexSource ? this.getDisplayedValue(this.selectedItems[0]) : this.selectedItems[0];
        const activeItemFromChangedSource = this.queryItemsList?.find(queryItem => queryItem.name === selectedItemName);
        if (typeof activeItemFromChangedSource === 'undefined') {
            this.keyManager?.setFirstItemActive();
        }
        else {
            this.keyManager?.setActiveItem(activeItemFromChangedSource);
        }
    }
    subscribeToPreselectedItems() {
        this.preselectedItemsSubscription = this.preselectedItems$.subscribe((items) => {
            this.selectedItems = items ? [...items] : [];
            this.handleFocusOnSourceChange();
            this.updateSelectAll();
        });
    }
    subscribeToOverlayOpenForAnimation() {
        this.overlayOpenSubscription = this.overlayOpen$.pipe(distinctUntilChanged()).subscribe(isOpen => {
            this.isOpen = isOpen;
            if (this.isOpen) {
                this.buildAndPlayAnimation(this.fadeIn(), 'showing');
            }
            else {
                this.buildAndPlayAnimation(this.fadeOut(), 'void');
            }
        });
    }
    fadeIn() {
        return [
            style({ opacity: 0, transform: 'scale(1, 0.8)' }),
            animate(`${FADE_ANIMATION_DURATION}ms cubic-bezier(0, 0, 0.2, 1)`, style({ opacity: 1, transform: 'scale(1, 1)' }))
        ];
    }
    fadeOut() {
        return [
            style({ opacity: '*', transform: '*' }),
            animate(`${FADE_ANIMATION_DURATION}ms ease-in-out`, style({ opacity: 0, transform: 'scale(1, 1)' }))
        ];
    }
    containsFocus() {
        const activeElement = _getFocusedElementPierceShadowDom();
        const activeIndex = this.queryItemsList?.toArray().findIndex(item => item.elementRef.nativeElement === activeElement);
        return Boolean(activeElement) && activeIndex > NEGATIVE_ONE;
    }
    buildAndPlayAnimation(animationMetadata, animationName) {
        switch (animationName) {
            case 'showing':
                this.fadeInFactory = this.animationBuilder.build(animationMetadata);
                this.fadeInPlayer = this.fadeInFactory.create(this.elementRef.nativeElement);
                this.fadeInPlayer.play();
                break;
            case 'void':
                this.fadeOutFactory = this.animationBuilder.build(animationMetadata);
                this.fadeOutPlayer = this.fadeOutFactory.create(this.elementRef.nativeElement);
                this.fadeOutPlayer.play();
                this.fadeOutPlayer.onDone(() => {
                    this.animationDone$.next(true);
                });
                break;
            default:
                break;
        }
    }
    getIndexOfItem(item) {
        let index;
        if (this.allowGroups) {
            index = this.selectedItems.findIndex(selectedItem => selectedItem === this.getGroupItemValue(item));
        }
        else if (this.hasComplexSource) {
            index = this.selectedItems.findIndex(selectedItem => selectedItem === this.getValueFor(item));
        }
        else {
            index = this.selectedItems.findIndex(selectedItem => selectedItem === item);
        }
        return index;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectListComponent, deps: [{ token: i0.Injector }, { token: i0.ChangeDetectorRef }, { token: i1.AnimationBuilder }, { token: ANIMATION_MODULE_TYPE }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillSelectListComponent, isStandalone: true, selector: "windmill-select-list", viewQueries: [{ propertyName: "selectListWrapper", first: true, predicate: ["selectListWrapper"], descendants: true }, { propertyName: "queryItemsList", predicate: i0.forwardRef(() => WindmillListItemComponent), descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  #selectListWrapper\r\n  cdkTrapFocus\r\n  class=\"select-list-wrapper height-{{ maxHeight }}\"\r\n  [class.centric-multiple-select-list]=\"multiple\">\r\n  @if (hasEmptyMessage) {\r\n    <div class=\"empty-message-container\">\r\n      <span> {{ sourceProperties.emptyMessage }}</span>\r\n    </div>\r\n  } @else {\r\n    <ul\r\n      class=\"select-list\"\r\n      role=\"listbox\"\r\n      [attr.id]=\"listIdProperties.listId\"\r\n      [attr.aria-label]=\"accessibilityProperties.ariaLabelList\"\r\n      [attr.aria-description]=\"accessibilityProperties.ariaDescriptionList\"\r\n      [attr.aria-labelledby]=\"accessibilityProperties.ariaLabelledbyList\"\r\n      [attr.aria-describedby]=\"accessibilityProperties.ariaDescribedbyList\"\r\n      [attr.data-testid]=\"listIdProperties.testListId\"\r\n      (keydown)=\"onKeyboardChange($event)\"\r\n      (focusin)=\"onFocusIn($event)\"\r\n      (focusout)=\"onFocusOut()\">\r\n      @if (selectAll && multiple) {\r\n        <windmill-list-item\r\n          role=\"option\"\r\n          [class.is-selected]=\"isSelectAllChecked\"\r\n          [attr.aria-checked]=\"getSelectAllAriaChecked()\"\r\n          [attr.aria-description]=\"accessibilityProperties.ariaDescriptionSelectAllCheckbox\"\r\n          [hasSelectAll]=\"hasSelectAll\"\r\n          [name]=\"selectAllLabel\"\r\n          [isSelected]=\"isSelectAllChecked\"\r\n          [isIndeterminate]=\"isSelectAllIndeterminate\"\r\n          (keydown)=\"onListItemKeydown($event)\"\r\n          (selectAllChanged)=\"onSelectAllChange($event)\">\r\n        </windmill-list-item>\r\n      }\r\n      @if (allowGroups) {\r\n        @for (groupItem of sourceGroups; track groupItem) {\r\n          <div\r\n            class=\"has-groups\"\r\n            role=\"group\"\r\n            [attr.aria-labelledby]=\"groupItem['id']\">\r\n            <span\r\n              class=\"group-subheader\"\r\n              role=\"presentation\"\r\n              [class.is-disabled]=\"groupItem['disabled']\"\r\n              [attr.id]=\"groupItem['id']\">\r\n              {{ groupItem['name'] }}\r\n            </span>\r\n            @for (item of groupItem['items']; track item) {\r\n              <windmill-list-item\r\n                role=\"option\"\r\n                [class.is-selected]=\"isItemSelected(item)\"\r\n                [class.is-disabled]=\"groupItem['disabled']\"\r\n                [class.multiple]=\"multiple\"\r\n                [attr.aria-checked]=\"getAriaChecked(item)\"\r\n                [attr.aria-selected]=\"getAriaSelected(item)\"\r\n                [attr.aria-disabled]=\"groupItem['disabled']\"\r\n                [attr.aria-description]=\"accessibilityProperties.ariaDescriptionListItem\"\r\n                [name]=\"item['name']\"\r\n                [multiple]=\"multiple\"\r\n                [isSelected]=\"isItemSelected(item)\"\r\n                [isDisabled]=\"isDisabled(groupItem)\"\r\n                (keydown)=\"onListItemKeydown($event)\"\r\n                (singleSelection)=\"updateSelectedItems(item)\"\r\n                (multipleSelection)=\"onMultipleSelection($event, item)\">\r\n              </windmill-list-item>\r\n            }\r\n          </div>\r\n        }\r\n      } @else {\r\n        @for (item of sourceProperties.source; track item) {\r\n          <windmill-list-item\r\n            role=\"option\"\r\n            [class.is-selected]=\"isItemSelected(item)\"\r\n            [attr.aria-checked]=\"getAriaChecked(item)\"\r\n            [attr.aria-selected]=\"getAriaSelected(item)\"\r\n            [attr.aria-description]=\"accessibilityProperties.ariaDescriptionListItem\"\r\n            [name]=\"getNameFor(item)\"\r\n            [multiple]=\"multiple\"\r\n            [isSelected]=\"isItemSelected(item)\"\r\n            (keydown)=\"onListItemKeydown($event)\"\r\n            (singleSelection)=\"updateSelectedItems(item)\"\r\n            (multipleSelection)=\"onMultipleSelection($event, item)\">\r\n          </windmill-list-item>\r\n        }\r\n      }\r\n    </ul>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "directive", type: CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "component", type: WindmillListItemComponent, selector: "windmill-list-item", inputs: ["name", "multiple", "isSelected", "isIndeterminate", "isDisabled", "hasSelectAll"], outputs: ["multipleSelection", "singleSelection", "selectAllChanged"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-select-list', encapsulation: ViewEncapsulation.None, imports: [CdkTrapFocus, WindmillListItemComponent], standalone: true, template: "<div\r\n  #selectListWrapper\r\n  cdkTrapFocus\r\n  class=\"select-list-wrapper height-{{ maxHeight }}\"\r\n  [class.centric-multiple-select-list]=\"multiple\">\r\n  @if (hasEmptyMessage) {\r\n    <div class=\"empty-message-container\">\r\n      <span> {{ sourceProperties.emptyMessage }}</span>\r\n    </div>\r\n  } @else {\r\n    <ul\r\n      class=\"select-list\"\r\n      role=\"listbox\"\r\n      [attr.id]=\"listIdProperties.listId\"\r\n      [attr.aria-label]=\"accessibilityProperties.ariaLabelList\"\r\n      [attr.aria-description]=\"accessibilityProperties.ariaDescriptionList\"\r\n      [attr.aria-labelledby]=\"accessibilityProperties.ariaLabelledbyList\"\r\n      [attr.aria-describedby]=\"accessibilityProperties.ariaDescribedbyList\"\r\n      [attr.data-testid]=\"listIdProperties.testListId\"\r\n      (keydown)=\"onKeyboardChange($event)\"\r\n      (focusin)=\"onFocusIn($event)\"\r\n      (focusout)=\"onFocusOut()\">\r\n      @if (selectAll && multiple) {\r\n        <windmill-list-item\r\n          role=\"option\"\r\n          [class.is-selected]=\"isSelectAllChecked\"\r\n          [attr.aria-checked]=\"getSelectAllAriaChecked()\"\r\n          [attr.aria-description]=\"accessibilityProperties.ariaDescriptionSelectAllCheckbox\"\r\n          [hasSelectAll]=\"hasSelectAll\"\r\n          [name]=\"selectAllLabel\"\r\n          [isSelected]=\"isSelectAllChecked\"\r\n          [isIndeterminate]=\"isSelectAllIndeterminate\"\r\n          (keydown)=\"onListItemKeydown($event)\"\r\n          (selectAllChanged)=\"onSelectAllChange($event)\">\r\n        </windmill-list-item>\r\n      }\r\n      @if (allowGroups) {\r\n        @for (groupItem of sourceGroups; track groupItem) {\r\n          <div\r\n            class=\"has-groups\"\r\n            role=\"group\"\r\n            [attr.aria-labelledby]=\"groupItem['id']\">\r\n            <span\r\n              class=\"group-subheader\"\r\n              role=\"presentation\"\r\n              [class.is-disabled]=\"groupItem['disabled']\"\r\n              [attr.id]=\"groupItem['id']\">\r\n              {{ groupItem['name'] }}\r\n            </span>\r\n            @for (item of groupItem['items']; track item) {\r\n              <windmill-list-item\r\n                role=\"option\"\r\n                [class.is-selected]=\"isItemSelected(item)\"\r\n                [class.is-disabled]=\"groupItem['disabled']\"\r\n                [class.multiple]=\"multiple\"\r\n                [attr.aria-checked]=\"getAriaChecked(item)\"\r\n                [attr.aria-selected]=\"getAriaSelected(item)\"\r\n                [attr.aria-disabled]=\"groupItem['disabled']\"\r\n                [attr.aria-description]=\"accessibilityProperties.ariaDescriptionListItem\"\r\n                [name]=\"item['name']\"\r\n                [multiple]=\"multiple\"\r\n                [isSelected]=\"isItemSelected(item)\"\r\n                [isDisabled]=\"isDisabled(groupItem)\"\r\n                (keydown)=\"onListItemKeydown($event)\"\r\n                (singleSelection)=\"updateSelectedItems(item)\"\r\n                (multipleSelection)=\"onMultipleSelection($event, item)\">\r\n              </windmill-list-item>\r\n            }\r\n          </div>\r\n        }\r\n      } @else {\r\n        @for (item of sourceProperties.source; track item) {\r\n          <windmill-list-item\r\n            role=\"option\"\r\n            [class.is-selected]=\"isItemSelected(item)\"\r\n            [attr.aria-checked]=\"getAriaChecked(item)\"\r\n            [attr.aria-selected]=\"getAriaSelected(item)\"\r\n            [attr.aria-description]=\"accessibilityProperties.ariaDescriptionListItem\"\r\n            [name]=\"getNameFor(item)\"\r\n            [multiple]=\"multiple\"\r\n            [isSelected]=\"isItemSelected(item)\"\r\n            (keydown)=\"onListItemKeydown($event)\"\r\n            (singleSelection)=\"updateSelectedItems(item)\"\r\n            (multipleSelection)=\"onMultipleSelection($event, item)\">\r\n          </windmill-list-item>\r\n        }\r\n      }\r\n    </ul>\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.ChangeDetectorRef }, { type: i1.AnimationBuilder }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }], propDecorators: { queryItemsList: [{
                type: ViewChildren,
                args: [forwardRef(() => WindmillListItemComponent)]
            }], selectListWrapper: [{
                type: ViewChild,
                args: ['selectListWrapper']
            }] } });

class WindmillSelectOverlayService {
    overlay;
    selectWidthChange = new BehaviorSubject(0);
    selectWidthChangeSubscription = Subscription.EMPTY;
    backdropClickEventSubscription = Subscription.EMPTY;
    keydownEventSubscription = Subscription.EMPTY;
    animationDoneSubscription = Subscription.EMPTY;
    listRef;
    overlayRef = null;
    animationDone = false;
    hasSelectAll = false;
    closed = new EventEmitter();
    positions = [
        {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 4
        },
        {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -4,
            panelClass: 'windmill-select-panel-above'
        }
    ];
    constructor(overlay) {
        this.overlay = overlay;
    }
    ngOnDestroy() {
        this.backdropClickEventSubscription.unsubscribe();
        this.keydownEventSubscription.unsubscribe();
        this.animationDoneSubscription.unsubscribe();
    }
    openSelectList(listProperties) {
        const overlayClass = listProperties.overlayClass ?? '';
        this.overlayRef = this.createOverlay(listProperties.elementRef, overlayClass);
        if (this.overlayRef) {
            const componentRef = this.attachSelectList(this.overlayRef);
            this.listRef = this.getInitializedComponentRefForList(listProperties, componentRef);
            this.subscribeToSelectWidthChange();
            this.handleListAnimation();
        }
        return this.listRef;
    }
    closeSelectList() {
        if (this.listRef.instance.animationMode === 'NoopAnimations') {
            this.overlayRef?.dispose();
        }
        this.selectWidthChangeSubscription.unsubscribe();
        this.listRef.instance.overlayOpen$.next(false);
    }
    getInitializedComponentRefForList(listProperties, componentRef) {
        const selectListComponent = componentRef.instance;
        this.initializeMultipleProperties(selectListComponent, listProperties);
        selectListComponent.allowGroups = listProperties.allowGroups;
        selectListComponent.width = listProperties.width;
        selectListComponent.maxHeight = listProperties.maxHeight;
        selectListComponent.accessibilityProperties = listProperties.accessibilityProperties;
        selectListComponent.listIdProperties = listProperties.listIdProperties;
        selectListComponent.sourceProperties = listProperties.sourceProperties;
        return componentRef;
    }
    initializeMultipleProperties(selectListComponent, listProperties) {
        selectListComponent.multiple = listProperties.multiple;
        selectListComponent.selectAll = listProperties.selectAll;
        selectListComponent.selectAllLabel = listProperties.selectAllLabel;
    }
    createOverlay(elementRef, overlayClass) {
        const positionStrategy = this.getPositionStrategy(elementRef);
        if (positionStrategy) {
            return this.overlay.create(new OverlayConfig({
                hasBackdrop: true,
                backdropClass: 'transparent-backdrop',
                panelClass: ['select-panel', overlayClass],
                positionStrategy
            }));
        }
        return null;
    }
    getPositionStrategy(elementRef) {
        const selectContainerNativeElement = elementRef.nativeElement;
        const selectContainer = selectContainerNativeElement.querySelector('.select-wrapper');
        if (selectContainer) {
            return this.overlay
                .position()
                .flexibleConnectedTo(selectContainer)
                .withPositions(this.positions)
                .withFlexibleDimensions(false)
                .withPush(false);
        }
        return null;
    }
    attachSelectList(overlayRef) {
        const selectPortal = new ComponentPortal(WindmillSelectListComponent);
        this.subscribeToBackdropClickEvent();
        this.subscribeToKeydownEvents();
        return overlayRef.attach(selectPortal);
    }
    subscribeToKeydownEvents() {
        if (this.overlayRef) {
            this.keydownEventSubscription = this.overlayRef.keydownEvents().subscribe(event => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                }
                if (event.key === 'Escape' || event.key === 'Tab') {
                    this.closed.emit(true);
                    this.listRef.instance.overlayOpen$.next(false);
                }
            });
        }
    }
    subscribeToBackdropClickEvent() {
        if (this.overlayRef) {
            this.backdropClickEventSubscription = this.overlayRef.backdropClick().subscribe(() => {
                this.closed.emit(true);
                this.listRef.instance.overlayOpen$.next(false);
            });
        }
    }
    subscribeToSelectWidthChange() {
        this.selectWidthChangeSubscription = this.selectWidthChange.subscribe(width => {
            if (width) {
                this.listRef.instance.width = width;
                this.listRef.instance.setListWidthInHtml();
                this.overlayRef?.updatePosition();
            }
        });
    }
    subscribeToAnimationDone() {
        this.animationDoneSubscription = this.listRef.instance.animationDone$.subscribe(isDone => {
            if (isDone) {
                this.animationDone = isDone;
                this.overlayRef?.dispose();
            }
        });
    }
    handleListAnimation() {
        this.listRef.instance.overlayOpen$.next(true);
        if (this.listRef.instance.animationMode !== 'NoopAnimations') {
            this.subscribeToAnimationDone();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectOverlayService, deps: [{ token: i1$1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectOverlayService, providedIn: WindmillSelectListComponent });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectOverlayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: WindmillSelectListComponent
                }]
        }], ctorParameters: () => [{ type: i1$1.Overlay }] });

const INITIAL_INDEX = -1;
const WINDMILL_SELECT_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => WindmillSelectComponent),
    multi: true
};
/**
 * To use this component in a module, you can import the `WindmillSelectModule`:
 * @example
 * ```typescript
 * import { WindmillSelectModule } from '@windmill/ng-windmill/select';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillSelectComponent`:
 * @example
 * ```typescript
 * import { WindmillSelectComponent } from '@windmill/ng-windmill/select';
 * ```
 */
class WindmillSelectComponent extends WindmillSelectPropertiesDirective {
    focusMonitor;
    selectOverlayService;
    injector;
    /**
     * Property can be used to add a new checkbox at the top with select all functionality
     */
    get selectAll() {
        return this._selectAll;
    }
    set selectAll(value) {
        this._selectAll = value;
        this.selectOverlayService.hasSelectAll = value;
    }
    /**
     * Property is used to set the readonly state for the component
     */
    get readonly() {
        return this._readonly;
    }
    set readonly(value) {
        this._readonly = value;
        if (value && this.isOpened) {
            this.closeOverlay();
        }
    }
    selectWrapper;
    elementRef;
    ariaLive = 'off';
    injectedFormControlSubscription = Subscription.EMPTY;
    get isEditable() {
        return !this.readonly && !this.disabled;
    }
    get role() {
        return this.readonly ? 'input' : 'combobox';
    }
    get isRequired() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    get selectWidth() {
        return this.elementRef.nativeElement.getBoundingClientRect().width;
    }
    constructor(focusMonitor, selectOverlayService, injector) {
        const render2 = injector.get(Renderer2);
        const elementRef = injector.get(ElementRef);
        super(render2, elementRef);
        this.focusMonitor = focusMonitor;
        this.selectOverlayService = selectOverlayService;
        this.injector = injector;
        this.elementRef = elementRef;
    }
    ngOnChanges(changes) {
        const isSourceChanged = typeof changes.source?.currentValue !== 'undefined';
        if (isSourceChanged) {
            this.writeValue(this.value);
            this.updateDisplayedValueForComplexSource();
            if (this.selectListComponentRef) {
                this.selectListComponentRef.instance.sourceProperties.source = this.source;
                this.passPreselectedItemsToOverlay();
            }
        }
    }
    ngOnInit() {
        this.injectedFormControl = this.injector.get(NgControl);
        if (this.injectedFormControl instanceof NgModel && this.injectedFormControl.valueChanges !== null) {
            this.injectedFormControlSubscription = this.injectedFormControl.valueChanges.pipe(distinctUntilChanged$1()).subscribe(() => {
                if (this.isOpened) {
                    this.passPreselectedItemsToOverlay();
                }
            });
        }
    }
    ngAfterViewInit() {
        this.focusMonitor.monitor(this.selectWrapper, true).subscribe((origin) => {
            this.focusOrigin = origin;
        });
    }
    ngOnDestroy() {
        if (this.isOpened) {
            this.closeOverlay();
        }
        this.focusMonitor.stopMonitoring(this.selectWrapper);
        this.injectedFormControlSubscription?.unsubscribe();
    }
    onPopState() {
        if (this.isOpened) {
            this.closeOverlay();
        }
    }
    writeValue(value) {
        this.value = this.getValidValue(value);
        const display = this.getDisplayedValue(value);
        this.displayedValue = Array.isArray(display) ? display.join(', ') : display;
        this.updateSelectedItemsOnInvalidFcValue(value);
        this.updateSelectedItems();
    }
    getValidValue(newValue) {
        const validValue = this.multiple
            ? Array.isArray(newValue)
                ? this.validValueMultiple(newValue)
                : this.validValueMultiple([newValue])
            : newValue;
        return validValue ?? null;
    }
    onClick() {
        this.openOverlay();
    }
    onFocusout(event) {
        if (event.relatedTarget === null || event.relatedTarget?.tagName === 'BUTTON') {
            this.onTouched();
            this.ariaLive = 'off';
        }
    }
    onKeydown(event) {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            this.handleKeyboard(event);
        }
        else if (['Enter', ' ', 'PageUp', 'PageDown'].includes(event.key) && this.hasEmptyMessage && this.isOpened) {
            event.preventDefault();
        }
    }
    handleKeyboard(event) {
        if (!this.isOpened && !this.selectOverlayService.overlayRef?.hostElement) {
            if (this.multiple) {
                if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
                    this.openOverlay();
                }
            }
            else {
                this.updateSelectOnKeydown(event.key);
            }
        }
    }
    onClearValue() {
        this.getFormControl().patchValue(null);
        this.injectedFormControl.control?.markAsDirty();
        this.injectedFormControl.control?.markAsTouched();
        this.selectionChange.emit(null);
        this.focusSelectInputButton();
        if ((this.multiple && this.displayedValue === '') || this.displayedValue === null) {
            this.counterValueChange.emit(this.counterValue);
        }
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    openOverlay() {
        this.destroyExistingAnimationPlayers();
        this.disposeExistingOverlay();
        const componentProperties = this.getComponentProperties();
        const listProperties = this.getSelectListProperties(componentProperties);
        this.openList(listProperties);
        this.subscribeToOverlayEvents();
        this.passPreselectedItemsToOverlay();
        this.initializeResizeObserver();
        this.ariaLive = 'off';
    }
    updateValue(newValue) {
        this.writeValue(newValue);
        this.onChange(newValue);
    }
    updateSelectedItemsOnInvalidFcValue(formControlValue) {
        const fcValueExistsInSource = typeof this.getItemByFormControlValue() !== 'undefined';
        if (!this.isValidValue(formControlValue) || !fcValueExistsInSource) {
            this.selectedItems = [];
        }
    }
    subscribeToOverlayEvents() {
        this.subscribeToSelectionChangeEvent();
        this.subscribeToClosedOverlayEvent();
        this.subscribeToScrollToBottomEvent();
    }
    unsubscribeToOverlayEvents() {
        this.selectionChangeSubscription.unsubscribe();
        this.closedOverlaySubscription.unsubscribe();
        this.scrolledToBottomSubscription.unsubscribe();
    }
    openList(listProperties) {
        this.selectListComponentRef = this.selectOverlayService.openSelectList(listProperties);
        this.selectOverlayService.hasSelectAll = this.selectAll;
        this.isOpened = true;
        this.openedChange.emit(this.isOpened);
    }
    getComponentProperties() {
        return {
            focusOrigin: this.focusOrigin,
            width: this.selectWidth,
            selectAll: this.selectAll,
            elementRef: this.elementRef
        };
    }
    closeOverlay() {
        this.isOpened = false;
        this.selectOverlayService.closeSelectList();
        this.openedChange.emit(this.isOpened);
        if (!this.hasItemsDisplayed) {
            this.focusSelectInputButton();
        }
        this.onTouched();
        this.unsubscribeToOverlayEvents();
        this.disconnectResizeObserver();
    }
    destroyExistingAnimationPlayers() {
        this.selectOverlayService.listRef?.instance.destroyExistingAnimationPlayers();
    }
    disposeExistingOverlay() {
        this.isOpened = false;
        this.selectOverlayService.overlayRef?.detach();
        this.selectOverlayService.overlayRef?.dispose();
        this.unsubscribeToOverlayEvents();
    }
    initializeResizeObserver() {
        this.resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                const width = entry.contentRect.width;
                this.selectOverlayService.selectWidthChange.next(width);
            });
        });
        this.resizeObserver.observe(this.selectWrapper.nativeElement);
    }
    disconnectResizeObserver() {
        this.resizeObserver.unobserve(this.selectWrapper.nativeElement);
        this.resizeObserver.disconnect();
    }
    subscribeToClosedOverlayEvent() {
        this.closedOverlaySubscription = this.selectOverlayService.closed.subscribe((isClosed) => {
            this.isOpened = !isClosed;
            if (isClosed) {
                this.closeOverlay();
            }
        });
    }
    subscribeToSelectionChangeEvent() {
        this.selectionChangeSubscription = this.selectListComponentRef.instance.selectionChange.subscribe((selectedItems) => {
            this.selectedItems = [...selectedItems];
            if (this.multiple) {
                this.updateValue(this.selectedItems);
                this.selectionChange.emit(this.selectedItems);
                this.counterValueChange.emit(this.counterValue);
            }
            else {
                const lastSelectedItem = this.selectedItems[this.selectedItems.length - 1];
                if (lastSelectedItem !== this.value) {
                    this.updateValue(lastSelectedItem);
                }
                this.selectionChange.emit(lastSelectedItem);
                this.closeOverlay();
            }
        });
    }
    subscribeToScrollToBottomEvent() {
        this.scrolledToBottomSubscription = this.selectListComponentRef.instance.scrolledToBottom.subscribe((scrollEvent) => this.scrolledToBottom.emit(scrollEvent));
    }
    passPreselectedItemsToOverlay() {
        this.updateSelectedItems();
        this.selectListComponentRef.instance.preselectedItems$.next(this.selectedItems);
    }
    updateSelectedItems() {
        const selectedItem = this.hasComplexSource ? this.getItemByFormControlValue() : this.value;
        if (typeof this.value === 'undefined') {
            this.selectedItems = [];
        }
        else {
            this.selectedItems =
                this.multiple && Array.isArray(selectedItem)
                    ? [...new Set(selectedItem)]
                    : selectedItem === 0 || selectedItem
                        ? [selectedItem]
                        : [];
        }
    }
    focusSelectInputButton() {
        const selectButton = this.elementRef.nativeElement.querySelector('.select-input');
        selectButton?.focus();
    }
    getDisplayedValue(value) {
        return this.multiple ? this.getMultipleDisplayedValue() : this.getSingleDisplayedValue(value);
    }
    getMultipleDisplayedValue() {
        if (this.allowGroups) {
            return this.getMultipleGroupsDisplayedValue();
        }
        else if (this.hasComplexSource)
            return this.getMultipleComplexDisplayedValue();
        return this.validValueMultiple(this.value);
    }
    getSingleDisplayedValue(value) {
        return this.allowGroups ? this.getGroupsDisplayedValue() : this.getSimpleDisplayedValue(value);
    }
    getSimpleDisplayedValue(value) {
        if (this.hasComplexSource) {
            const foundItem = this.source?.find(item => this.doesValueExistsInSource(item, value));
            return foundItem ? foundItem[this.displayPropertyName] : null;
        }
        return this.source?.find(item => item === value);
    }
    validValueMultiple(value) {
        const validValue = [];
        value?.forEach(valueItem => {
            const itemSource = this.findValueInSource(valueItem);
            validValue.push(itemSource);
        });
        return validValue.filter(item => typeof item !== 'undefined');
    }
    getMultipleGroupsDisplayedValue() {
        if (Array.isArray(this.value)) {
            return this.value
                .map(fcValue => {
                let itemToBeDisplayed;
                this.source?.forEach(element => {
                    const searchItem = element.items?.find((item) => item.value === fcValue);
                    if (searchItem) {
                        itemToBeDisplayed = searchItem;
                    }
                });
                return itemToBeDisplayed ? itemToBeDisplayed.name : null;
            })
                .filter(item => item !== null);
        }
        return null;
    }
    getMultipleComplexDisplayedValue() {
        if (Array.isArray(this.value)) {
            return this.value
                .map(fcValue => {
                const itemToBeDisplayed = this.source?.find(item => this.getValueComplex(item) === fcValue);
                return itemToBeDisplayed ? this.getDisplayedComplexValue(itemToBeDisplayed) : null;
            })
                .filter(item => item !== null);
        }
        const itemToBeDisplayed = this.source?.find(item => item[this.valuePropertyName] === this.value);
        return itemToBeDisplayed ? itemToBeDisplayed[this.displayPropertyName] : null;
    }
    getGroupsDisplayedValue() {
        let groupItem;
        this.source?.forEach((group) => {
            group['items']?.forEach((option) => {
                if (option.value === this.value) {
                    groupItem = option;
                }
            });
        });
        return groupItem ? groupItem.name : null;
    }
    updateSelectOnKeydown(keyName) {
        this.ariaLive = 'polite';
        const source = this.allowGroups ? this.getGroupListItems() : this.source;
        const indexOfCurrentDisplayedValue = this.getIndexOfDisplayedValue();
        if (keyName === 'ArrowDown' && source && indexOfCurrentDisplayedValue < source.length - 1) {
            this.updateSelectedItemWhenPressArrow(indexOfCurrentDisplayedValue, 1);
        }
        else if (keyName === 'ArrowUp' && indexOfCurrentDisplayedValue > 0) {
            this.updateSelectedItemWhenPressArrow(indexOfCurrentDisplayedValue, INITIAL_INDEX);
        }
        else if (keyName === 'Home' || keyName === 'End') {
            this.updateSelectedItemWhenPressHomeOrEnd(keyName);
        }
    }
    updateSelectedItemWhenPressArrow(indexOfCurrentDisplayedValue, step) {
        const groupItemsList = this.allowGroups ? this.getGroupListItems() : [];
        const item = this.allowGroups
            ? groupItemsList[indexOfCurrentDisplayedValue + step]
            : this.source?.[indexOfCurrentDisplayedValue + step];
        this.updateSelectedItemWithDisplayedValue(item);
    }
    updateSelectedItemWhenPressHomeOrEnd(keyName) {
        const groupItemsList = this.allowGroups ? this.getGroupListItems() : [];
        const itemIndex = keyName === 'Home' ? 0 : this.allowGroups ? groupItemsList.length - 1 : this.source ? this.source.length - 1 : null;
        if (itemIndex !== null) {
            const item = this.allowGroups ? groupItemsList[itemIndex] : this.source?.[itemIndex];
            this.updateSelectedItemWithDisplayedValue(item);
        }
    }
    updateSelectedItemWithDisplayedValue(item) {
        this.displayedValue = this.getDisplayedSelectedValue(item);
        this.value = this.getFormControlSelectedValue(item);
        this.updateValue(this.allowGroups || this.hasComplexSource ? this.value : this.displayedValue);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectComponent, deps: [{ token: i1$2.FocusMonitor }, { token: WindmillSelectOverlayService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillSelectComponent, isStandalone: true, selector: "windmill-select", inputs: { selectAll: "selectAll", readonly: "readonly" }, host: { listeners: { "window:popstate": "onPopState()" } }, providers: [WINDMILL_SELECT_VALUE_ACCESSOR, WindmillSelectOverlayService], viewQueries: [{ propertyName: "selectWrapper", first: true, predicate: ["selectWrapper"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div\r\n  #selectWrapper\r\n  class=\"select-wrapper\"\r\n  [class.has-value]=\"shouldShowValue\"\r\n  [class.has-clear-icon]=\"hasClearIcon\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-invalid]=\"hasInvalidState\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [ngClass]=\"size\">\r\n  @if (isEditable) {\r\n    <button\r\n      role=\"combobox\"\r\n      type=\"button\"\r\n      aria-haspopup=\"listbox\"\r\n      class=\"select-input\"\r\n      [class.has-counter]=\"shouldShowCounter\"\r\n      [attr.id]=\"id\"\r\n      [attr.title]=\"title\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.aria-expanded]=\"isOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-required]=\"isRequired\"\r\n      [attr.aria-invalid]=\"hasInvalidState\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      [style.backgroundColor]=\"backgroundColorValue\"\r\n      (click)=\"onClick()\"\r\n      (focusout)=\"onFocusout($event)\"\r\n      (keydown)=\"onKeydown($event)\">\r\n      <span class=\"select-input-content\">\r\n        @if (shouldShowValue) {\r\n          <span\r\n            class=\"value\"\r\n            [attr.aria-live]=\"ariaLive\">\r\n            {{ displayedValue }}\r\n          </span>\r\n        } @else {\r\n          <span class=\"placeholder\">{{ placeholder }}</span>\r\n        }\r\n        @if (shouldShowCounter) {\r\n          <span\r\n            class=\"multiple-select-counter\"\r\n            [attr.aria-label]=\"ariaLabelCounter\">\r\n            {{ counterValue }}\r\n          </span>\r\n        }\r\n      </span>\r\n      <centric-icon2\r\n        class=\"arrow-icon\"\r\n        [svgIcon]=\"'chevron-large-down_r'\"\r\n        [size]=\"arrowIconSize\"></centric-icon2>\r\n    </button>\r\n    @if (shouldShowClearButton) {\r\n      <button\r\n        class=\"clear-button\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"ariaLabelClearButton\"\r\n        (click)=\"onClearValue()\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_r'\"\r\n          [size]=\"'16'\"></centric-icon2>\r\n      </button>\r\n    }\r\n  } @else {\r\n    <div\r\n      class=\"windmill-input\"\r\n      [class.is-disabled]=\"disabled\"\r\n      [class.is-readonly]=\"readonly\"\r\n      [class.has-value]=\"shouldShowValue\"\r\n      [ngClass]=\"size\">\r\n      <input\r\n        #inputReadonlyElement\r\n        type=\"text\"\r\n        class=\"input-element\"\r\n        [attr.role]=\"role\"\r\n        [attr.readonly]=\"readonly\"\r\n        [attr.disabled]=\"disabledAttributeValue\"\r\n        [attr.value]=\"displayedValue\"\r\n        [attr.id]=\"id\"\r\n        [attr.title]=\"title\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.placeholder]=\"nonEditablePlaceholder\"\r\n        [attr.aria-expanded]=\"ariaExpandedValue\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.aria-disabled]=\"disabledAttributeValue\" />\r\n      @if (disabled) {\r\n        <centric-icon2\r\n          class=\"arrow-icon\"\r\n          [svgIcon]=\"'chevron-large-down_r'\"\r\n          [size]=\"arrowIconSize\">\r\n        </centric-icon2>\r\n      }\r\n    </div>\r\n  }\r\n</div>\r\n@if (hasInvalidState) {\r\n  <div class=\"centric-error-messages\">\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  </div>\r\n}\r\n", dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "ngmodule", type: ScrollingModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-select', encapsulation: ViewEncapsulation.None, imports: [A11yModule, CommonModule, FormsModule, NgClass, ReactiveFormsModule, ScrollingModule, CentricIcon2Component], providers: [WINDMILL_SELECT_VALUE_ACCESSOR, WindmillSelectOverlayService], standalone: true, template: "<div\r\n  #selectWrapper\r\n  class=\"select-wrapper\"\r\n  [class.has-value]=\"shouldShowValue\"\r\n  [class.has-clear-icon]=\"hasClearIcon\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.is-invalid]=\"hasInvalidState\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [ngClass]=\"size\">\r\n  @if (isEditable) {\r\n    <button\r\n      role=\"combobox\"\r\n      type=\"button\"\r\n      aria-haspopup=\"listbox\"\r\n      class=\"select-input\"\r\n      [class.has-counter]=\"shouldShowCounter\"\r\n      [attr.id]=\"id\"\r\n      [attr.title]=\"title\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.aria-expanded]=\"isOpened\"\r\n      [attr.aria-controls]=\"listId\"\r\n      [attr.aria-required]=\"isRequired\"\r\n      [attr.aria-invalid]=\"hasInvalidState\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\"\r\n      [style.backgroundColor]=\"backgroundColorValue\"\r\n      (click)=\"onClick()\"\r\n      (focusout)=\"onFocusout($event)\"\r\n      (keydown)=\"onKeydown($event)\">\r\n      <span class=\"select-input-content\">\r\n        @if (shouldShowValue) {\r\n          <span\r\n            class=\"value\"\r\n            [attr.aria-live]=\"ariaLive\">\r\n            {{ displayedValue }}\r\n          </span>\r\n        } @else {\r\n          <span class=\"placeholder\">{{ placeholder }}</span>\r\n        }\r\n        @if (shouldShowCounter) {\r\n          <span\r\n            class=\"multiple-select-counter\"\r\n            [attr.aria-label]=\"ariaLabelCounter\">\r\n            {{ counterValue }}\r\n          </span>\r\n        }\r\n      </span>\r\n      <centric-icon2\r\n        class=\"arrow-icon\"\r\n        [svgIcon]=\"'chevron-large-down_r'\"\r\n        [size]=\"arrowIconSize\"></centric-icon2>\r\n    </button>\r\n    @if (shouldShowClearButton) {\r\n      <button\r\n        class=\"clear-button\"\r\n        type=\"button\"\r\n        [attr.aria-label]=\"ariaLabelClearButton\"\r\n        (click)=\"onClearValue()\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_r'\"\r\n          [size]=\"'16'\"></centric-icon2>\r\n      </button>\r\n    }\r\n  } @else {\r\n    <div\r\n      class=\"windmill-input\"\r\n      [class.is-disabled]=\"disabled\"\r\n      [class.is-readonly]=\"readonly\"\r\n      [class.has-value]=\"shouldShowValue\"\r\n      [ngClass]=\"size\">\r\n      <input\r\n        #inputReadonlyElement\r\n        type=\"text\"\r\n        class=\"input-element\"\r\n        [attr.role]=\"role\"\r\n        [attr.readonly]=\"readonly\"\r\n        [attr.disabled]=\"disabledAttributeValue\"\r\n        [attr.value]=\"displayedValue\"\r\n        [attr.id]=\"id\"\r\n        [attr.title]=\"title\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.placeholder]=\"nonEditablePlaceholder\"\r\n        [attr.aria-expanded]=\"ariaExpandedValue\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.aria-disabled]=\"disabledAttributeValue\" />\r\n      @if (disabled) {\r\n        <centric-icon2\r\n          class=\"arrow-icon\"\r\n          [svgIcon]=\"'chevron-large-down_r'\"\r\n          [size]=\"arrowIconSize\">\r\n        </centric-icon2>\r\n      }\r\n    </div>\r\n  }\r\n</div>\r\n@if (hasInvalidState) {\r\n  <div class=\"centric-error-messages\">\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  </div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i1$2.FocusMonitor }, { type: WindmillSelectOverlayService }, { type: i0.Injector }], propDecorators: { selectAll: [{
                type: Input
            }], readonly: [{
                type: Input
            }], selectWrapper: [{
                type: ViewChild,
                args: ['selectWrapper']
            }], onPopState: [{
                type: HostListener,
                args: ['window:popstate']
            }] } });

const declaredModuleComponents = [
    WindmillListItemComponent,
    WindmillSelectComponent,
    WindmillSelectListComponent,
    WindmillSelectPropertiesDirective
];
class WindmillSelectModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectModule, imports: [A11yModule,
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ScrollingModule,
            CentricIcon2Module, WindmillListItemComponent,
            WindmillSelectComponent,
            WindmillSelectListComponent,
            WindmillSelectPropertiesDirective], exports: [WindmillSelectComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectModule, providers: [Overlay, WindmillSelectOverlayService], imports: [A11yModule,
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            ScrollingModule,
            CentricIcon2Module, WindmillSelectComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        A11yModule,
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        ScrollingModule,
                        CentricIcon2Module,
                        ...declaredModuleComponents
                    ],
                    exports: [WindmillSelectComponent],
                    providers: [Overlay, WindmillSelectOverlayService],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillSelectComponent, WindmillSelectModule };
